import Parse from 'parse';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setVehiclesInStore } from '../../actions/drivers';
import { updateToastInfo } from '../../actions/settings';
import { updateSpinnerState } from '../../actions/spinner';
import {
  getVehiclesListInZone,
  updateVehiclesInfo,
} from '../../parse-functions/vehicles';
import { convertArrayToObj } from '../../utils/json';
import { returnVehicleListTableColumns } from './vehicleUtils';
import ConfirmationModal from '../../components/common/confirmationModal';
import VehicleUpdateModal from './vehicleUpdateModal';
import ProcessingModal from './ProcessingModal';
import { faRefresh } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BreadCrumbTitle from '../scheme-management/Components/common/BreadCrumbTitle';
import { Button, ButtonGroup } from '@themesberg/react-bootstrap';
import ExcelDownloadReport from '../../components/common/excel/download';
import TripsTableData from '../trips/tripsTableData';
import SchemeInfoModal from '../scheme-management/Components/Scheme/SchemeInfoModal';
import { infoTable } from '../../utils/sms/infoTable';
import VehicleDetailsUpdateModal from './VehicleDetailsUpdateModal';
import VehiclePaymentsUpdateModal from './VehiclePaymentsUpdateModal';
import VehicleEditConfirmationModal from './VehicleEditConfirmationModal';

const VendorsList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isAdmin = useSelector(
    (state) => state?.user?.preferences?.isAdmin || false
  );
  // const isWHPaymentUser = useSelector(
  //   (state) => state?.user?.preferences?.isWHPaymentUser || false
  // );
  const zone = useSelector((state) => state?.user?.warehouse?.zone || '');
  const vehiclesList = useSelector(
    (state) => state?.drivers?.vehiclesList || {}
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showVehicleUpdateModal, setShowVehicleUpdateModal] = useState(false);
  const [showPaymentUpdateModal, setShowPaymentUpdateModal] = useState(false);
  const [showVehicleConfirmModal, setShowVehicleConfirmModal] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [updateMode, setUpdateMode] = useState('');
  const [editData, setEditData] = useState('');
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoData, setInfoData] = useState([]);
  const [tripsCount, setTripsCount] = useState({
    invoiced: 0,
    unpaid: 0,
  });
  useEffect(() => {
    if (Object.keys(vehiclesList) < 1 && zone) {
      getAvailableVehicleList();
    }
  }, [zone]);
  const getAvailableVehicleList = async () => {
    dispatch(updateSpinnerState(true));
    try {
      const filters = {
        zone,
        // status: "active",
        getAllVehicles: isAdmin,
      };
      let vehicles = await getVehiclesListInZone(filters);
      dispatch(
        setVehiclesInStore(
          convertArrayToObj(JSON.parse(JSON.stringify(vehicles)), 'objectId')
        )
      );
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'success',
          title: t('Vehicle List fetched'),
          message: t('All the Vehicles for the zone are listed here'),
        })
      );
    } catch (e) {
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t('Error'),
          message: t(e.message),
        })
      );
    }
  };

  const onRefreshClick = () => {
    getAvailableVehicleList();
  };

  const handleShowDeleteModal = () => {
    setShowDeleteModal(true);
  };
  const handleShowVehicleUpdateModal = () => {
    setShowVehicleUpdateModal(true);
  };
  const handleSelectedRowsUpdate = (actionId, data) => {
    // we can do edit and delete here
    setEditData(data);
    switch (actionId) {
      case 'Delete': {
        setUpdateMode('Delete');
        handleShowDeleteModal();
        break;
      }
      case 'Edit Vehicle Details': {
        handleShowVehicleUpdateModal();
        setUpdateMode('EditVehicle');
        break;
      }
      case 'Edit Payment Details': {
        setShowPaymentUpdateModal(true);
        setUpdateMode('EditPayment');
        break;
      }
      case 'Activate': {
        setUpdateMode('Activate');
        handleShowDeleteModal();
        break;
      }
      case 'Action Timeline': {
        setInfoData(data.actionLog);
        setShowInfoModal(true);
      }
      default: {
        break;
      }
    }
  };
  const handleOnConfirmClick = async (data) => {
    try {
      if (data[0].fromDate || data[0].toDate) {
        setShowProcessingModal(true);
      } else {
        dispatch(updateSpinnerState(true));
      }
      await updateVehiclesInfo({ data });
      await getAvailableVehicleList();
      setShowProcessingModal(false);
      setShowVehicleConfirmModal(false);
      setShowPaymentUpdateModal(false);
      setShowDeleteModal(false);
      setShowVehicleUpdateModal(false);
      setEditData('');
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'success',
          title: `Vehicles ${updateMode} Successful`,
          message: '',
        })
      );
    } catch (e) {
      setShowProcessingModal(false);
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: 'Vehicle Update Failed',
          message: e.message ? e.message : 'Please contact support for help',
        })
      );
    }
  };
  const handleModalOnConfirmClick = () => {
    const vehicleData = [];
    // eslint-disable-next-line no-unused-expressions
    [editData]?.forEach((vehicle) => {
      const data = {};
      data.objectId = vehicle.objectId;
      data.dataFields = {
        status: updateMode === 'Delete' ? 'deleted' : 'active',
        actionLog: {
          action: updateMode === 'Delete' ? 'deleted' : 'activated',
          user: Parse.User.current().getUsername(),
          time: new Date().getTime(),
        },
      };
      data.updateType = 'StatusUpdate';
      vehicleData.push(data);
    });
    handleOnConfirmClick(vehicleData);
  };
  const handleVehicleUpdateInfo = async () => {
    try {
      dispatch(updateSpinnerState(true));
      const count = await updateVehiclesInfo({
        data: [{ ...editData, updateType: updateMode }],
        needDataCount: true,
      });
      setTripsCount(count);
      setShowVehicleConfirmModal(true);
    } catch (e) {
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: 'Vehicle Update Failed',
          message: e.message ? e.message : 'Please contact support for help',
        })
      );
    } finally {
      dispatch(updateSpinnerState(false));
    }
  };

  const handleConfirmModalClick = () => {
    const vehicleObject = editData;
    const vehicleUpdateData = [
      {
        objectId: vehicleObject.objectId,
        dataFields: {
          contactNumber: parseInt(vehicleObject.contactNumber),
          driverName: vehicleObject.driverName,
          paymentMode: vehicleObject.paymentMode,
          vehicleType: vehicleObject.vehicleType,
          vendorName: vehicleObject.vendorName,
          vendorObjectId: vehicleObject.vendorObjectId,
          paymentAmount:
            vehicleObject.paymentMode === 'perTrip'
              ? 0
              : parseInt(vehicleObject.paymentAmount || 0),
          vehicleNumber: vehicleObject.vehicleNumber,
          zone: vehicleObject.zone,
          storeName: vehicleObject.storeName,
          actionLog: {
            action: 'updated',
            user: Parse.User.current().getUsername(),
            time: new Date().getTime(),
          },
        },
        fromDate: vehicleObject.fromDate,
        toDate: vehicleObject.toDate,
        updateType: updateMode,
      },
    ];
    handleOnConfirmClick(vehicleUpdateData);
  };
  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <BreadCrumbTitle breadcrumbs={[{ title: 'Vehicle List' }]} />
        </div>
        <ButtonGroup>
          <Button onClick={onRefreshClick} variant="outline-primary" size="sm">
            <FontAwesomeIcon icon={faRefresh} />
            &nbsp;&nbsp;{t('Refresh')}
          </Button>
          <ExcelDownloadReport
            data={Object.values(vehiclesList) || []}
            fileName={`vehiclesList`}
          />
        </ButtonGroup>
      </div>
      <TripsTableData
        data={vehiclesList}
        columns={returnVehicleListTableColumns(handleSelectedRowsUpdate)}
        page={'vehiclesList'}
      />

      <ConfirmationModal
        showModal={showDeleteModal}
        onConfirm={handleModalOnConfirmClick}
        closeModal={() => {
          setShowDeleteModal(false);
        }}
        modalBody={t(
          `Are you sure you want to ${updateMode?.toLowerCase()} these vehicles`
        )}
        modalTitle={t(`${updateMode} Vehicle(s)`)}
      />
      {showVehicleUpdateModal && (
        <VehicleDetailsUpdateModal
          closeModal={() => {
            setShowVehicleUpdateModal(false);
          }}
          vehicle={editData || {}}
          showModal={showVehicleUpdateModal}
          onConfirm={handleVehicleUpdateInfo}
          setEditData={setEditData}
        />
      )}

      {showPaymentUpdateModal && (
        <VehiclePaymentsUpdateModal
          closeModal={() => {
            setShowPaymentUpdateModal(false);
          }}
          vehicle={editData || {}}
          showModal={showPaymentUpdateModal}
          onConfirm={handleVehicleUpdateInfo}
          setEditData={setEditData}
        />
      )}
      <ProcessingModal
        closeModal={() => {
          setShowProcessingModal(false);
        }}
        showModal={showProcessingModal}
        message={'Updating data. Please Wait...'}
      />

      <VehicleEditConfirmationModal
        updateMode={updateMode}
        vehicle={editData}
        tripsCount={tripsCount}
        onConfirm={handleConfirmModalClick}
        closeModal={() => setShowVehicleConfirmModal(false)}
        showModal={showVehicleConfirmModal}
      />

      <SchemeInfoModal
        showModal={showInfoModal}
        closeModal={() => setShowInfoModal(false)}
        data={[...infoData].reverse()}
        columns={infoTable()}
        title={'Vehicle Activity Log'}
      />
    </>
  );
};
export default VendorsList;
